// Agrupamento no orçamento ("Dia 1", "Pré-evento", "Day Use").
//
// O modelo tem DUAS peças:
//
//   budget.budgetGroups   → quais agrupamentos existem e em que ordem
//   ServiceData.groupId   → a que agrupamento cada serviço pertence
//
// A linha exibida dentro de um agrupamento é o par (fornecedor, categoria)
// DERIVADO dos serviços que estão nele. Não há registro de linha em lugar
// nenhum: criar uma linha é criar um serviço nela, e apagar o último serviço
// tira a linha de lá. Isso é o que mantém a visão agrupada sempre coerente com
// a visão plana, sem lista paralela para sincronizar.
//
// `addedItemTypeList` entra por UM motivo só, e apenas em "Sem agrupamento":
// categoria que ficou sem nenhum serviço continua visível na visão plana, e
// precisa continuar visível aqui também. Ela NÃO carrega `groupId` — pendurar
// um ali fez a visão plana duplicar o fornecedor e a proteção contra
// duplicidade reescrever o id com `|dup|N`, que aparecia como "código" no lugar
// do nome. `supplierList` não participa de jeito nenhum.

import {
  AddedTypes,
  BudgetGroup,
  BudgetItemType,
  CalcOption,
} from "../types/budget/budget";
import { resolveServiceCalcOption } from "./budget-phase-calc-option";

/**
 * Id do agrupamento *pseudo* que recolhe o que não está em agrupamento nenhum.
 *
 * Não é gravado e nunca aparece em `budgetGroups`: existe só na renderização,
 * para o orçamento antigo (nenhum serviço agrupado) ter onde ser exibido.
 */
export const UNGROUPED_ID = "__ungrouped__";

export const UNGROUPED_NAME = "Sem agrupamento";

/**
 * Nome de agrupamento para COMPARAÇÃO.
 *
 * Espelha o `normalizeGroupName` do backend (`mapping-apply.util.ts`), que é
 * quem casa o serviço com o seu bloco na geração da planilha do cliente:
 * espaço sobrando e caixa diferente não podem separar um agrupamento do seu
 * bloco — o template da BRF tem "1 - CENOGRAFIA E EQUIPAMENTOS " com espaço no
 * fim, e ninguém digitaria isso de novo. Espaço repetido no meio também não: o
 * PME do SOMOS tem "AGÊNCIA (NF)  - SERVIÇOS INTERNOS", com dois.
 *
 * Vive aqui, e não em cada tela, porque a comparação acontece em três lugares
 * (o editor do template do cliente, o "trazer agrupamentos" e o reducer) e uma
 * cópia que divergisse faria o agrupamento parar de casar em silêncio.
 */
export function normalizeGroupName(name: unknown): string {
  return typeof name === "string"
    ? name.trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR")
    : "";
}

type VersionedService = {
  supplierId?: string;
  type?: string;
  calcOption?: string;
  groupId?: string;
  versions?: number[];
  budgetValues?: { calcOption?: string };
  negotiationValue?: Array<{ calcOption?: string } | undefined>;
  closingValues?: { calcOption?: string };
};

/** Chave da LINHA: agrupamento + fornecedor + categoria. */
export function rowKey(row: {
  type: string;
  supplierId: string;
  groupId?: string;
}): string {
  return `${row.groupId ?? UNGROUPED_ID}::${row.supplierId}::${row.type}`;
}

export type RenderableGroup = {
  id: string;
  name: string;
  /** `false` no pseudo-agrupamento: não pode ser arrastado, renomeado nem excluído. */
  isReal: boolean;
  /** Índice absoluto em `budgetGroups`; `-1` no pseudo-agrupamento. */
  absoluteIndex: number;
};

export type RenderableRow = {
  supplierId: string;
  type: BudgetItemType;
  /** Agrupamento em que a linha está sendo desenhada; ausente = "Sem agrupamento". */
  groupId?: string;
};

type Params = {
  budgetGroups: BudgetGroup[] | undefined;
  budgetServices: VersionedService[] | undefined;
  addedItemTypeList: AddedTypes[] | undefined;
  calcOption: CalcOption;
  version: number;
  status?: string;
};

/**
 * Linhas de UM agrupamento, na ordem em que os serviços aparecem.
 *
 * "Sem agrupamento" recolhe três coisas:
 *
 * 1. os serviços sem vínculo;
 * 2. os ÓRFÃOS — serviços apontando para um agrupamento que não existe mais.
 *    Sem esse caminho o serviço não apareceria em lugar nenhum e o valor sumiria
 *    da tela, que é o pior tipo de perda: silenciosa;
 * 3. as categorias sem serviço NENHUM em toda a forma de cobrança. A visão
 *    plana continua mostrando a categoria vazia depois que o último serviço é
 *    removido; esconder aqui faria a mesma tela contar duas histórias.
 */
export function getRowsOfGroup(
  params: Params & { groupId: string }
): RenderableRow[] {
  const {
    budgetGroups,
    budgetServices,
    addedItemTypeList,
    calcOption,
    version,
    status,
    groupId,
  } = params;
  const isUngrouped = groupId === UNGROUPED_ID;
  const stage = { version, status };

  const existingIds = new Set(
    (budgetGroups ?? [])
      .filter((group) => group.calcOption === calcOption)
      .map((group) => group.id)
  );

  const seen = new Set<string>();
  const rows: RenderableRow[] = [];

  (budgetServices ?? []).forEach((service) => {
    if (resolveServiceCalcOption(service, stage) !== calcOption) return;
    if (!(service.versions ?? [0]).includes(version)) return;

    const orphan = !!service.groupId && !existingIds.has(service.groupId);
    const belongs = isUngrouped
      ? !service.groupId || orphan
      : service.groupId === groupId;
    if (!belongs) return;

    const row: RenderableRow = {
      supplierId: String(service.supplierId),
      type: String(service.type) as BudgetItemType,
      groupId: isUngrouped ? undefined : groupId,
    };
    const key = rowKey(row);
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  });

  if (!isUngrouped) return rows;

  // Categoria sem serviço nenhum na forma de cobrança. "Nenhum" é a condição —
  // categoria cujos serviços estão todos no "Dia 1" pertence ao Dia 1, e
  // repeti-la aqui vazia seria uma linha fantasma.
  const pairsWithService = new Set(
    (budgetServices ?? [])
      .filter(
        (service) => resolveServiceCalcOption(service, stage) === calcOption
      )
      .map((service) => `${service.supplierId}::${service.type}`)
  );

  (addedItemTypeList ?? []).forEach((item) => {
    if (item.calcOption !== calcOption) return;
    if (pairsWithService.has(`${item.supplierId}::${item.type}`)) return;

    const row: RenderableRow = {
      supplierId: String(item.supplierId),
      type: String(item.type) as BudgetItemType,
    };
    const key = rowKey(row);
    if (seen.has(key)) return;
    seen.add(key);
    rows.push(row);
  });

  return rows;
}

/**
 * Agrupamentos a exibir, na ordem, para a forma de cobrança e a etapa atuais.
 *
 * - agrupamento excluído nesta etapa (`deletedInVersions`) não aparece;
 * - "Sem agrupamento" só aparece quando sobra alguma linha para ele — e sempre
 *   por último;
 * - na aba Orçamento o agrupamento VAZIO aparece (o usuário acabou de criá-lo);
 *   nas etapas versionadas ele fica oculto, porque ali é ruído herdado.
 */
export function getVisibleGroups(params: Params): RenderableGroup[] {
  const { budgetGroups, calcOption, version } = params;
  const groups: RenderableGroup[] = [];

  (budgetGroups ?? []).forEach((group, absoluteIndex) => {
    if (group.calcOption !== calcOption) return;
    if ((group.deletedInVersions ?? []).includes(version)) return;
    if (
      version !== 0 &&
      getRowsOfGroup({ ...params, groupId: group.id }).length === 0
    ) {
      return;
    }

    groups.push({
      id: group.id,
      name: group.name,
      isReal: true,
      absoluteIndex,
    });
  });

  if (getRowsOfGroup({ ...params, groupId: UNGROUPED_ID }).length > 0) {
    groups.push({
      id: UNGROUPED_ID,
      name: UNGROUPED_NAME,
      isReal: false,
      absoluteIndex: -1,
    });
  }

  return groups;
}
